import {derived, get, Readable, Writable, writable} from 'svelte/store'
import makeId from '../../lib/makeId'
import fetchWithTimeout from '../../lib/fetchWithTimeout'
import { extractProducts } from '../../lib/extractProducts'
import {dispatchWishlistUpdate} from '../Channels/global-events'
import type {IProduct, IProductVariant, IShowMessage} from './types/Show'
import {IShow, IShowValues, ShowState} from './types/Show'
import type {IBid, IBidData, INotification, IUser} from './types/Bids'
import {AgentPanel, BidState, ButtonState, PaymentType} from './types/Bids'
import displayPrice from '../../lib/displayPrice'
import setUserIdGa4 from "../../lib/setUserIdGa4";

// show

const showStore = <Writable<IShow>>writable(<IShow>{
    canBid: false,
    state: ShowState.Init
})

const valuesStore = <Writable<IShowValues|null>>writable(null)

const productsStore = <Writable<Array<IProduct>>>writable([])

const nextProductsStore = <Writable<Array<IProduct>>>writable([])

const currentProductIdStore = <Writable<string|null>>writable(null)

const currentProductStore = <Readable<IProduct|null>>derived(
    [productsStore, currentProductIdStore],
    ([$products, $currentProductId]) => {
        let filtered = $products.filter(product => product.productId === $currentProductId)
        return filtered.length === 0 ? null : filtered[0]
    }
)

export interface ILastProductsStore extends Readable<Array<IProduct>> {
    load(): Promise<any>,
    add(product: IProduct): void,
    addToWishlist(sku: string),
    setIsOnWishlist(isOnWishlist: boolean, sku: string): void,
}

const lastProductsStore = <ILastProductsStore>(() => {
    
    const maxProducts = 10

    const { subscribe, update } = <Writable<Array<IProduct>>>writable([])

    const add = (product: IProduct) => {
        update((lastProducts: Array<IProduct>) => {
            if(lastProducts.findIndex((lastProduct) => lastProduct.productId === product.productId) > -1) {
                return lastProducts
            }
            lastProducts.unshift(product)
            if(lastProducts.length > maxProducts) {
                lastProducts.pop()
            }
            return lastProducts
        })
    }

    const load = async () => {
        const url = 'https://api.juwelo.de/api/no-auth/app/games/de/offset/0?options=itemsPerPage:'+maxProducts
        try {
            return fetchWithTimeout(url, {
                timeout: 2000,
            })
                .then((response) => {
                    if(!response.ok) {
                        return null
                    }
                    response.json().then(data => {
                        // must reverse items to add them in the right order, newest is last(!)
                        data.gameItems.reverse() 
                        data.gameItems.forEach((gameItem) => {
                            let showProducts = extractProducts(gameItem.products)
                            showProducts.reverse() // show can have multiple products, newest is also last
                            showProducts.map((product) => add(product))
                        })
                    })
                })
        } catch(error) {
            console.error(error)
        }
    }
     
    const addToWishlist = async (sku: string) => {

        const addToWishlistUrl = globalThis.config.baseUrl+'/ws/wishlist/add/sku/'+sku+'/form_key/'+get(sessionStore).token
        try {
            fetchWithTimeout(addToWishlistUrl, {timeout: 15000, credentials: 'include'})
                .then(response => response.ok ? response.json() : null)
                .then(wishListResponse => {
                    if(!wishListResponse) {
                        return
                    }
                    setIsOnWishlist(true, sku)
                    if (wishListResponse?.numberOfItems) {
                        dispatchWishlistUpdate(wishListResponse.numberOfItems)
                    }
                })
        } catch(error) {
            console.error(error)
        }
    }

    const setIsOnWishlist = (isOnWishlist: boolean, sku: string) => {
        update((lastProducts: Array<IProduct>) => {
            const product = lastProducts.find((product) => product.productId === sku)
            if(product) {
                product.isOnWishlist = isOnWishlist
            }
            return lastProducts
        })
    }

    return <ILastProductsStore>{
        subscribe: subscribe,
        add: add,
        load: load,
        addToWishlist: addToWishlist
    }
})()

export interface IShowMessageStore extends Readable<IShowMessage|null> {
    set(message: IShowMessage): void,
}

const showMessageStore = <IShowMessageStore>(() => {

    const { subscribe, set } = <Writable<IShowMessage|null>>writable(null)
    
    let messageTimeout = null
    let currentMessage: IShowMessage = null
    
    const setMessage = (message: IShowMessage|null) => {
        if( message && 
            currentMessage && 
            message.priority < currentMessage.priority
        ) {
            return
        }
        
        set(message)
        currentMessage = message
        
        if(messageTimeout) {
            window.clearTimeout(messageTimeout)
        }
        if(message?.durationMs) {
            messageTimeout = window.setTimeout(() => { 
                
                set(null)
                currentMessage = null
                
            }, message.durationMs)
        }
    }
    
    return {
        subscribe: subscribe,
        set: setMessage
    }
})()

// notifications

export interface INotificationsStore extends Readable<Array<INotification>> {
    add(notification: INotification): void,
    remove(id: string): void
}

const notificationsStore = <INotificationsStore>(() => {
    
    const { subscribe, update } = <Writable<Array<INotification>>>writable([])
    
    const add = (notification: INotification) => {
        notification = {...notification, id: makeId(16)} // make sure to copy this into a unique object
        
        update((notifications: Array<INotification>) => {
            notifications.push(notification)
            if(notifications.length > 3) {
                notifications.shift()   
            }
            return notifications
        })
        
        if(notification.durationMs > 0) {
            window.setTimeout(() => {
                remove(notification.id)
            }, notification.durationMs)
        }
    }
    const remove = (id: string) => {
        update((notifications: Array<INotification>) => {
            notifications = notifications.filter((notification: INotification) => notification.id !== id)
            return notifications
        })
    } 
    return {
        subscribe: subscribe,
        add: add,
        remove: remove
    }
})()

// button

const buttonStore = <Writable<ButtonState>>writable(ButtonState.none)

// session

export enum LoginResult {
    invalidLogin = 'invalidLogin',
    invalidToken = 'invalidToken',
    authenticated = 'authenticated',
    error = 'error',
}

export enum CompleteDataResult {
    error = 'error',
    success = 'success',
    invalidToken= 'invalidToken',
    invalidPhoneEmpty = 'invalidPhoneEmpty',
    invalidPhoneSyntax = 'invalidPhoneSyntax',
}

export enum RegisterResult {
    error = 'error',
    success = 'success',
    invalidToken = 'invalidToken',
    invalidEmailSyntax = 'invalidEmailSyntax',
    invalidEmailAlreadyInUse = 'invalidEmailAlreadyInUse',
    invalidPasswordTooShort = 'invalidPasswordTooShort',
    invalidPasswordsNotEqual = 'invalidPasswordsNotEqual',
    invalidOther = 'invalidOther',
}

export interface ISessionStore extends Readable<IUser> {
    start(): void, // start to periodically check session status (and thus keep session alive)
    stop(): void, // stop to periodically check session status
    setHasBid(): void,
    login(username: string, password: string, autoLogin: boolean): Promise<LoginResult>,
    completeData(phone: string, paymentType: PaymentType): Promise<CompleteDataResult>,
    register(
        gender: string, 
        title: string, 
        firstName: string, 
        lastName: string, 
        street: string, 
        postalCode: string,
        city: string,
        countryCode: string,
        email: string,
        password: string,
        passwordConfirmation: string
    ): Promise<RegisterResult>,
    getFormKey(): string|null
}

const sessionStore = <ISessionStore>(() => {
    const { subscribe, set, update } = <Writable<IUser>>writable({
        authenticated: false, 
        bidReady: false,
        hasBidBefore: false,
        token: null,
        uuid: null
    })
    
    const unsubscribeUuid = subscribe(session => {
        if(session.uuid) {
            setUserIdGa4(session.uuid)
            unsubscribeUuid()
        }
    })
    
    // status check
    
    let statusCheckInterval = null
    let isStatusCheck = false
    let formKey = null
    
    const checkStatus = () => {
        if(isStatusCheck) {
            return
        }
        isStatusCheck = true
        let url = globalThis.config.baseUrl+'/bidagent/status.php'
        try {
            fetchWithTimeout(url, {
                timeout: 2000,
                credentials: 'include'
            })
                .then(result => result.json())
                .then(data => {
                    if(!data) {
                        data = { authenticated: false, bidReady: false, hasBidBefore: false, token: null, uuid: null } 
                    }
                    set(data)
                    formKey = data.token
                })
                .finally(() => { isStatusCheck=false })
        } catch(error) {
            console.error(error)
        }
    }    
    const stop = () => {
        if(statusCheckInterval) {
            window.clearInterval(statusCheckInterval)
        }
    }
    const start = () => {
        stop()
        checkStatus()
        statusCheckInterval = window.setInterval(checkStatus, 15000)
    }
    const login = async (username: string, password: string, autoLogin: boolean) => {
        try {
            let url = globalThis.config.baseUrl+'/bidagent/app/login'

            const formData = new FormData()
            formData.append('username', username)
            formData.append('password', password)
            formData.append('form_key', formKey)
            if(autoLogin) {
                formData.append('auto', '1')
            }
            
            let data = await fetch(url, { 
                credentials: 'include', 
                method: 'POST',
                body: formData
            }).then(result => result.json())
            
            if(!data) {
                return LoginResult.error
            }
            if(data.error === 'invalid_token') {
                return LoginResult.invalidToken
            }
            
            set(data)
            formKey = data.token
            if(data.authenticated) {
                return LoginResult.authenticated
            } else {
                return LoginResult.invalidLogin
            }
            
        } catch(error) {
            console.error(error)
            return LoginResult.error
        }
    }
    const completeData = async (phone: string, paymentType: PaymentType) => {
        try {
            let url = globalThis.config.baseUrl+'/bidagent/app/completeData'
    
            const formData = new FormData()
            formData.append('phone', phone)
            formData.append('paymentType', paymentType)
            formData.append('form_key', formKey)

            let data = await fetch(url, {
                credentials: 'include',
                method: 'POST',
                body: formData
            }).then(result => result.json())
            
            if(!data) {
                return CompleteDataResult.error
            }
            
            if(data.error === 'invalid_token') {
                return CompleteDataResult.invalidToken
            }
            
            if(data.status === 'success') {
                
                set(data.session)
                formKey = data.session.token
                return CompleteDataResult.success
                
            } else if(data.status === 'invalid') {
                
                if(data.phone === 'invalid_empty') {
                    return CompleteDataResult.invalidPhoneEmpty
                } else {
                    return CompleteDataResult.invalidPhoneSyntax
                }
            }

        } catch(error) {
            console.error(error)
        }
        
        return CompleteDataResult.error
    }
    const setHasBid = () => {
        update(data => {
            data.hasBidBefore = true
            return data
        })
    }
    const register = async (
        gender: string,
        title: string,
        firstName: string,
        lastName: string,
        street: string,
        postalCode: string,
        city: string,
        countryCode: string,
        email: string,
        password: string,
        passwordConfirmation: string
    ) => {
        try {
            let url = globalThis.config.baseUrl+'/bidagent/app/register'

            const formData = new FormData()
            formData.append('form_key', formKey)
            formData.append('gender', gender)
            formData.append('title', title)
            formData.append('firstName', firstName)
            formData.append('lastName', lastName)
            formData.append('street', street)
            formData.append('postalCode', postalCode)
            formData.append('city', city)
            formData.append('countryCode', countryCode)
            formData.append('email', email)
            formData.append('password', password)
            formData.append('passwordConfirmation', passwordConfirmation)
            
            let data = await fetch(url, {
                credentials: 'include',
                method: 'POST',
                body: formData
            }).then(result => result.json())

            if(!data) {
                return RegisterResult.error
            }

            if(data.error === 'invalid_token') {
                return RegisterResult.invalidToken
            }

            if(data.status === 'success') {

                set(data.session)
                formKey = data.session.token
                return RegisterResult.success

            } else if(data.status === 'invalid_email_syntax') {
                return RegisterResult.invalidEmailSyntax
                
            } else if(data.status === 'invalid_email_already_in_use') {
                return RegisterResult.invalidEmailAlreadyInUse
                
            } else if(data.status === 'invalid_password_too_short') {
                return RegisterResult.invalidPasswordTooShort
                
            } else if(data.status === 'invalid_passwords_not_equal') {
                return RegisterResult.invalidPasswordsNotEqual
                
            } else if(data.status === 'invalid_other') {
                return RegisterResult.invalidOther
            }
            
        } catch(error) {
            console.error(error)
        }
        
        return RegisterResult.error
    }
    
    const getFormKey = () => {
        return formKey
    }
    
    return {
        subscribe: subscribe,
        start: start,
        stop: stop,
        login: login,
        completeData: completeData,
        setHasBid: setHasBid,
        register: register,
        getFormKey: getFormKey,
    }
})()

// bidding and bids

export interface IBidStore extends Readable<Array<IBid>> {
    start(intervalMs: number): void, // start to periodically update bids
    stop(): void, // stop to periodically update bids
    addBid(bid: IBid): void,
    updateBids(): void, // queue a bid update (independently of the periodical updates)
    prepareBid(showId: number, productId: string, variantId: string): Promise<void>,
    confirmBid(showId: number, productId: string, variantId: string, priceCent: number): Promise<IBidData|null>,
    bookItem(productId: string, variantId: string, source: string): Promise<IBidData|null>,
    trackBidConversion(showId: number, bidId: string): Promise<object|null>,
}

const bidStore = <IBidStore>(() => {
    const self = <Writable<Array<IBid>>>writable([])
    const { subscribe, set, update } = self
    
    const prepareBid = async (showId: number, productId: string, variantId: string): Promise<void> => {
        try {
            let url = globalThis.config.baseUrl+'/bidagent/app/prepareBid'

            const formKey = sessionStore.getFormKey()
            const formData = new FormData()
            formData.append('showId', showId.toString())
            formData.append('productId', productId)
            formData.append('variantId', variantId)
            formData.append('form_key', formKey)

            await fetchWithTimeout(url, {
                timeout: 3000,
                credentials: 'include',
                method: 'POST',
                body: formData
            })

        } catch(error) {
            console.error(error)
        }
    }
    const confirmBid = async (showId: number, productId: string, variantId: string, priceCent: number): Promise<IBidData|null> => {
        try {
            let url = globalThis.config.baseUrl+'/bidagent/app/confirmBid'

            const formKey = sessionStore.getFormKey()
            const formData = new FormData()
            formData.append('showId', showId.toString())
            formData.append('productId', productId)
            formData.append('variantId', variantId)
            formData.append('priceCent', priceCent.toString())
            formData.append('form_key', formKey)

            const bidData = await fetchWithTimeout(url, {
                timeout: 15000,
                credentials: 'include',
                method: 'POST',
                body: formData
            }).then(result => result.json())
            
            return bidData

        } catch(error) {
            console.error(error)
        }
        
        return null
    }
    const bookItem = async (productId: string, variantId: string, source: string): Promise<IBidData|null> => {
        try {
            let url = import.meta.env.MODE === 'production' 
                ? globalThis.config.baseUrl+'/bidagent/app/book'
                : globalThis.config.baseUrl+'/bidagent/app/fakeBook'

            const formKey = sessionStore.getFormKey()
            const formData = new FormData()
            formData.append('productId', productId)
            formData.append('variantId', variantId)
            formData.append('form_key', formKey)
            formData.append('source', source)

            const bidData = await fetchWithTimeout(url, {
                timeout: 15000,
                credentials: 'include',
                method: 'POST',
                body: formData
            }).then(result => result.json())
            
            return bidData

        } catch(error) {
            console.error(error)
        }
        
        return null
    }

    const recentlyTrackedBidIds = [];
    const trackBidConversion = async (showId: number, bidId: string): Promise<object|null> => {
        try {
            if(recentlyTrackedBidIds.includes(bidId)) {
                return null
            }
            recentlyTrackedBidIds.push(bidId)
            
            const url = globalThis.config.baseUrl+'/bidagent/app/trackBidConversion'

            const formKey = sessionStore.getFormKey()
            const formData = new FormData()
            formData.append('showId', showId.toString())
            formData.append('bidId', bidId)
            formData.append('form_key', formKey)

            const trackingData = await fetchWithTimeout(url, {
                timeout: 15000,
                credentials: 'include',
                method: 'POST',
                body: formData
            }).then(result => result.json())
            
            await updateBids()
            
            return trackingData

        } catch(error) {
            console.error(error)
        }

        return null
    }
    const addBid = (bid: IBid) => {
        update(bids => {
            bids.unshift(bid)
            return bids
        })
    }
    
    let bidUpdateInterval: number = null
    let isBidUpdate: boolean = false
    
    const updateBidsList = async (bidsData: Array<IBidData>) => {
        try {
            const bids = get(self)
            const fetchProductIds = []
            
            let updateStore = false
            
            const bidsById = {}
            
            // update existing bids or schedule to fetch data to add bid
            
            bidsData.forEach(bidData => {
                
                // lookup bid in store
                let bid = bids.filter((b) => b.id === bidData.id)[0] || null
                
                if(bid) {
                    // bid exists -> update values
                    
                    if(bid.state !== bidData.state) {
                        if(bid.state === BidState.pending) {
                            bid.allowNotify = true
                        }
                        bid.state = BidState[bidData.state]
                        updateStore = true
                    }
                    if(bid.hasTracked !== bidData.hasTracked) {
                        bid.hasTracked = bidData.hasTracked
                        updateStore = true
                    }
                    if(bid.finalPrice?.cent !== bidData.finalPriceCent && bidData.finalPriceCent) {
                        bid.finalPrice = {
                            cent: bidData.finalPriceCent,
                            text: displayPrice(bidData.finalPriceCent)
                        }
                        updateStore = true
                    }
                    bidsById[bid.id] = bid
                    
                } else {
                    // otherwise: store for later fetch
                    if(fetchProductIds.indexOf(bidData.productId) < 0) {
                        fetchProductIds.push(bidData.productId)
                    }
                }
            })
            
            // fetch product data for new bids and add them to the store
            
            if(fetchProductIds.length > 0) {
                
                let url = 'https://api.juwelo.de/api/no-auth/app/products/'
                    +globalThis.locale.language
                    +'?filter=productIDs:'+fetchProductIds.join(',')
                    +'&options=includeUnavailable:true;allowZeroPrice:true'
                
                const products = await fetchWithTimeout(url, {
                    timeout: 15000
                }).then(result => result.json())
    
                bidsData.forEach(bidData => {
    
                    let bid = bids.filter(b => b.id === bidData.id)[0] || null
                    if(bid) {
                        return
                    }
                    let product = products.filter(product => product.productBase.productId === bidData.productId)[0] || null
                    if(!product) {
                        return
                    }
                    
                    bid = <IBid>{
                        id: bidData.id,
                        showId: bidData.showId,
                        state: bidData.state,
                        allowNotify: bidData.state === BidState.pending,
                        notified: false,
                        bidPrice: {
                            cent: bidData.priceCent,
                            text: displayPrice(bidData.priceCent)
                        },
                        hasTracked: bidData.hasTracked,
                        product: {
                            productId: bidData.productId,
                            availableVariantAspects: {},
                            name: product.localisation.productDetails.productName,
                            image: {
                                size80: 'https://cdn.media.juwelo.de/small/'+bidData.productId+'.jpg',
                                size300: 'https://cdn.media.juwelo.de/large/'+bidData.productId+'.jpg',
                                size800: 'https://cdn.media.juwelo.de/huge/'+bidData.productId+'.jpg',
                            },
                            type: product.localisation.productDetails.type,
                            available: false,
                            brand: null,
                            material: null,
                            materialWeight: null,
                            caratWeight: null,
                            designType: null,
                            dimensions: null,
                            gemStones: [],
                            variants: []
                        },
                        variant: {
                            available: false,
                            variantId: bidData.variantId,
                        }
                    }
                    if(bidData.material) {
                        bid.variant.material = bidData.material
                    }
                    if(bidData.ringsize) {
                        bid.variant.ringsize = bidData.ringsize
                    }
                    if(bidData.finalPriceCent) {
                        bid.finalPrice = {
                            cent: bidData.finalPriceCent,
                            text: displayPrice(bidData.finalPriceCent)
                        }
                    }
                    updateStore = true
                    bidsById[bid.id] = bid
                })
            }            
            
            if(updateStore) {
                const bidsUpdate = []
                bidsData.forEach(bidData => {
                    if(bidsById[bidData.id]) {
                        bidsUpdate.push(bidsById[bidData.id])
                    }
                })
                set(bidsUpdate)
            }
            
        } catch(error) {
            console.error(error)
        }
    }
    const updateBids = () => {
        if(isBidUpdate) {
            return
        }
        isBidUpdate = true
        let url = globalThis.config.baseUrl+'/bidagent/bids.php'
        try {
            fetchWithTimeout(url, {
                timeout: 3000,
                credentials: 'include'
            })
                .then(result => result.json())
                .then(data => {
                    if(!Array.isArray(data)) {
                        return
                    }
                    updateBidsList(data)
                })
                .finally(() => { isBidUpdate=false })
        } catch(error) {
            console.error(error)
        }
    }
    const stop = () => {
        if(bidUpdateInterval) {
            window.clearInterval(bidUpdateInterval)
        }
    }
    const start = (intervalMs: number) => {
        stop()
        updateBids()
        bidUpdateInterval = window.setInterval(updateBids, intervalMs)
    }
    
    return {
        start: start,
        stop: stop,
        addBid: addBid,
        updateBids: updateBids,
        subscribe: subscribe,
        prepareBid: prepareBid, 
        confirmBid: confirmBid,
        bookItem: bookItem,
        trackBidConversion: trackBidConversion,
    }
})()

// current variant (selection)

export interface ICurrentVariant {
    variant: IProductVariant|null
    material: string|null
    ringsize: string|null
}

export interface ICurrentVariantStore extends Readable<ICurrentVariant> {
    selectVariant(variantId: string|null),
    selectMaterial(material: string|null),
    selectRingsize(ringsize: string|null),
}

const currentVariantStore = <ICurrentVariantStore>(() => {

    const { subscribe, update } = <Writable<ICurrentVariant>>writable({
        variant: null,
        ringsize: null,
        material: null
    })
    
    const selectVariant = (variantId: string|null) => {
        if(!variantId) {
            update(current => {
                current.variant = null
                return current
            })
            return
        }
        let product = get(currentProductStore)
        if(!product) {
            return
        }
        let match = product.variants.filter((productVariant) => productVariant.variantId === variantId)
        if(match.length === 0) {
            return
        }            
        let variantMatch = match[0]
        update(current => {
            current.variant = variantMatch
            
            // only align with current variant if the variant has these aspects (to make it sticky as possible)
            // i.e. a pendant will not override the last chosen ringsize
            
            if(variantMatch.ringsize) {
                current.ringsize = variantMatch.ringsize
            }
            if(variantMatch.material) {
                current.material = variantMatch.material
            }
            return current
        })
    }
    
    const selectVariantIfAspectsMatch = (material: string|null, ringsize: string|null): IProductVariant|null => {
        let product = get(currentProductStore)
        if(!product) {
            return null
        }
        let match = product.variants.filter((productVariant) => {
            if(productVariant.material && material !== productVariant.material) {
                return false
            }
            if(productVariant.ringsize && ringsize !== productVariant.ringsize) {
                return false
            }
            return true
        })
        if(match.length > 0 && match[0].available) {
            return match[0]
        }
        return null
    }
    
    const selectMaterial = (material: string|null) => {
        let product = get(currentProductStore)
        if(!product) {
            return
        }
        if(material && product.variants.filter((productVariant) => productVariant.material === material).length === 0) {
            return
        }
        update(current => {
            current.material = material
            current.variant = selectVariantIfAspectsMatch(current.material, current.ringsize)
            return current
        })
    }
    
    const selectRingsize = (ringsize: string|null) => {
        let product = get(currentProductStore)
        if(!product) {
            return
        }
        if(ringsize && product.variants.filter((productVariant) => productVariant.ringsize === ringsize).length === 0) {
            return
        }
        update(current => {
            current.ringsize = ringsize
            current.variant = selectVariantIfAspectsMatch(current.material, current.ringsize)
            return current
        })
    }
    
    return {
        subscribe: subscribe,
        selectVariant: selectVariant,
        selectMaterial: selectMaterial,
        selectRingsize: selectRingsize,
    }
})()

// display-mode (show, login, register)

const agentPanelStore = <Writable<AgentPanel>>writable(AgentPanel.show)

export {
    showStore,
    valuesStore,
    currentProductStore,
    currentVariantStore,
    currentProductIdStore,
    productsStore,
    nextProductsStore,
    lastProductsStore,
    showMessageStore,
    notificationsStore,
    sessionStore,
    buttonStore,
    agentPanelStore,
    bidStore,
}